import { Card, Spinner } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ApiError, type FlowRunResponse, getFlowOptions, getFlowRunOptions } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { CenteredScreen } from "@/components/CenteredScreen";
import { type Crumb, PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { formatDateTime } from "@/i18n/format";
import { FlowRunAwaitingApproval } from "./FlowRunAwaitingApproval";
import { FlowRunReview } from "./FlowRunReview";
import { FlowRunStepCard, type FlowRunStepView, fromLiveStep, fromRunStep, stepNames } from "./FlowRunStepCard";
import { FlowStatusBadge } from "./flowMeta";
import { spaceWorkflowPath } from "./flowRunPaths";
import { useLiveFlowRun } from "./useLiveFlowRun";
import { useRefreshRunViews } from "./useRefreshRunViews";

const ACTIVE = new Set(["pending", "running"]);
// Not in background tabs (TanStack pauses intervals there): keep it that way, every tab counts toward the rate limit.
const POLL_MS = 5_000;

/**
 * Run detail: the run's input, each step's status and output, and the final output. A run started from this tab
 * shows live (useLiveFlowRun); otherwise a running run is polled. A paused run asks for a review.
 *
 * Opened from a space (`space`), it sits under that space (`{Space} / {Workflow} / {run}`), not the Build area.
 */
export function FlowRunDetailPage({ space }: Readonly<{ space?: { uuid: string; crumb: Crumb } }> = {}) {
  const { t } = useTranslation("workflows");
  const areas = useAreaCrumbs();
  const { uuid = "", runUuid = "" } = useParams();
  const live = useLiveFlowRun(uuid, runUuid);
  const run = useQuery({
    ...getFlowRunOptions({ path: { flow_uuid: uuid, run_uuid: runUuid } }),
    refetchInterval: (q) => (!live && ACTIVE.has(q.state.data?.data.status ?? "") ? POLL_MS : false),
  });
  const flow = useQuery(getFlowOptions({ path: { flow_uuid: uuid } }));
  const me = useCurrentUser();
  useRefreshWhenStatusChanges(uuid, run.data?.data.status);
  const flowName = flow.data?.data.name ?? "";

  if (run.isPending) {
    return (
      <CenteredScreen>
        <Spinner size="lg" />
      </CenteredScreen>
    );
  }
  if (run.isError) {
    return (
      <CenteredScreen>
        <Text tone="error" role="alert">
          {run.error instanceof ApiError && run.error.status === 404 ? t("runs.notFound") : t("runs.loadFailed")}
        </Text>
      </CenteredScreen>
    );
  }

  const data = run.data.data;
  const status = live ? "running" : data.status;
  const names = stepNames(flow.data?.data);
  const steps: FlowRunStepView[] = live
    ? // Parallel branches start in any order: keep the workflow's order, as the saved run has it.
      [...live.steps].sort((a, b) => a.stepNumber - b.stepNumber).map((s) => fromLiveStep(s, names))
    : (data.steps ?? []).map(fromRunStep);
  const pausedStep = data.status === "paused" ? (data.steps ?? []).find((s) => s.status === "paused") : undefined;
  // Paused for a write action its owner approves in the inbox (MHS-972), rather than for a review
  const awaitingApproval = !live && Boolean(pausedStep?.sideEffects?.waiting_on_action_request);
  const isOwner = data.createdByUuid === me.data?.user.uuid;
  const canReview = !live && !awaitingApproval && data.status === "paused" && (flow.data?.data.canEdit || isOwner);

  return (
    <div className="flex flex-col">
      <PageBreadcrumbs
        trail={
          space
            ? [space.crumb, { label: flowName, to: spaceWorkflowPath(space.uuid, uuid) }]
            : [
                areas.build,
                { label: t("title"), to: "/build/workflows" },
                { label: flowName, to: `/build/workflows/${uuid}` },
                { label: t("runs.title"), to: `/build/workflows/${uuid}/runs` },
              ]
        }
        current={formatDateTime(data.startedAt ?? data.createdAt)}
        actions={<FlowStatusBadge status={status} />}
      />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-6 md:px-8">
        <Card>
          <div className="flex flex-col gap-1">
            <Text variant="body">{t("runs.input")}</Text>
            <Text variant="body-sm" tone="secondary" className="whitespace-pre-wrap">
              {data.inputPrompt}
            </Text>
          </div>
        </Card>
        {live && live.totalSteps > 0 && (
          <Text variant="caption" tone="tertiary" role="status">
            {t("runs.stepsProgress", { current: Math.max(live.steps.length, 1), total: live.totalSteps })}
          </Text>
        )}
        {steps.length === 0 && ACTIVE.has(status) && (
          <Text variant="body-sm" tone="tertiary" role="status">
            {t("runs.waiting")}
          </Text>
        )}
        {steps.map((step) => (
          <FlowRunStepCard key={step.key} step={step} />
        ))}
        {canReview && <FlowRunReview flowUuid={uuid} runUuid={runUuid} output={pausedStep?.outputContent ?? ""} />}
        {awaitingApproval && <FlowRunAwaitingApproval isOwner={isOwner} />}
        <RunError data={data} liveError={live?.error} shownOnAStep={steps.map((st) => st.error)} />
      </div>
    </div>
  );
}

/** A polled run that pauses or ends changes what its lists and space cards show: refresh them. */
function useRefreshWhenStatusChanges(flowUuid: string, status: string | undefined) {
  const refreshRunViews = useRefreshRunViews();
  const seen = useRef(status);
  useEffect(() => {
    if (status && seen.current && status !== seen.current) void refreshRunViews(flowUuid);
    seen.current = status;
  }, [status, flowUuid, refreshRunViews]);
}

function RunError({
  data,
  liveError,
  shownOnAStep,
}: Readonly<{ data: FlowRunResponse; liveError?: string; shownOnAStep: (string | undefined)[] }>) {
  const message = liveError ?? data.errorMessage;
  // A run that failed on a step usually repeats that step's error: say it once.
  if (!message || shownOnAStep.includes(message)) return null;
  return (
    <Card>
      <Text variant="body-sm" tone="error">
        {message}
      </Text>
    </Card>
  );
}
